import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'utils/currencyFormatter';
import { getOrderStatus } from 'constants';
import './OrderControlTable.scss';

const OrderControlTable = ({ filters, orders = [], isLoading = false }) => {
  const { t } = useTranslation(['backoffice']);
  
  // Obtener los estados traducidos
  const orderStatuses = getOrderStatus(t);
  
  // Función para obtener el estado traducido
  const getStatusLabel = (statusId) => {
    if (!statusId) return 'N/A';
    const status = orderStatuses.find(s => s.id === statusId);
    return status ? status.value : statusId;
  };

  const formatDate = (date) => {
    if (!date) return '--';
    return date;
  };

  // Los datos ya vienen directamente como artículos desde el nuevo endpoint
  const tableData = useMemo(() => {
    return orders.map((article) => {
      // Parsear el descuento que viene como string "0.00 €"
      let descuento = 0;
      if (article.descuento) {
        const descuentoStr = article.descuento.toString().replace('€', '').replace(/\s/g, '').replace(',', '.');
        descuento = parseFloat(descuentoStr) || 0;
      }
      
      // Formatear fechas (pueden venir como "-" o como fecha)
      const fechaPedido = article.fecha_pedido && article.fecha_pedido !== '-' 
        ? article.fecha_pedido.split('T')[0] 
        : null;
      const fechaPrevistaEnvio = article.fecha_prevista_envio && article.fecha_prevista_envio !== '-' 
        ? article.fecha_prevista_envio.split('T')[0] 
        : null;
      const fechaEnvio = article.fecha_envio && article.fecha_envio !== '-' 
        ? article.fecha_envio.split('T')[0] 
        : null;
      
      return {
        id: article.id,
        biblioteca: article.biblioteca || 'N/A',
        ean: article.ean || 'N/A',
        titulo: article.titulo || 'N/A',
        fechaPedido,
        fechaPrevistaEnvio,
        fechaEnvio,
        estado: article.estado || 'N/A',
        editorial: article.editorial || 'N/A',
        autor: article.autor || 'N/A',
        descuento,
        cantidad: parseInt(article.cantidad || 0)
      };
    });
  }, [orders]);

  // Los filtros ahora se aplican en el backend, así que mostramos todos los datos que llegan
  const filteredData = tableData;

  return (
    <div className="order-control-table">
      {/* Leyenda */}
      <div className="order-control-table__legend">
        <div className="order-control-table__legend-item">
          <div className="order-control-table__legend-dot order-control-table__legend-dot--pedido"></div>
          <span className="order-control-table__legend-text">
            {t('table.fechaPedido', { ns: 'backoffice' })}
          </span>
        </div>
        <div className="order-control-table__legend-item">
          <div className="order-control-table__legend-dot order-control-table__legend-dot--prevista"></div>
          <span className="order-control-table__legend-text">
            {t('table.fechaPrevistaEnvio', { ns: 'backoffice' })}
          </span>
        </div>
        <div className="order-control-table__legend-item">
          <div className="order-control-table__legend-dot order-control-table__legend-dot--envio"></div>
          <span className="order-control-table__legend-text">
            {t('table.fechaEnvio', { ns: 'backoffice' })}
          </span>
        </div>
      </div>

      {/* Tabla */}
      <div className="order-control-table__container">
        <Table striped bordered hover className="order-control-table__table">
          <thead>
            <tr>
              <th>{t('table.biblioteca', { ns: 'backoffice' })}</th>
              <th>{t('table.ean', { ns: 'backoffice' })}</th>
              <th>{t('table.titulo', { ns: 'backoffice' })}</th>
              <th>{t('table.fechas', { ns: 'backoffice' })}</th>
              <th>{t('table.estado', { ns: 'backoffice' })}</th>
              <th>{t('table.editorial', { ns: 'backoffice' })}</th>
              <th>{t('table.autor', { ns: 'backoffice' })}</th>
              <th>{t('table.dto', { ns: 'backoffice' })}</th>
              <th>{t('table.cant', { ns: 'backoffice' })}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9} className="text-center p-5">
                  <Spinner animation="border" role="status" />
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center p-5">
                  {t('table.noResults', { ns: 'backoffice' })}
                </td>
              </tr>
            ) : (
              filteredData.map((item) => (
              <tr key={item.id}>
                <td>{item.biblioteca}</td>
                <td>{item.ean}</td>
                <td>{item.titulo}</td>
                <td className="order-control-table__dates">
                  <div className="order-control-table__date order-control-table__date--pedido">
                    {formatDate(item.fechaPedido)}
                  </div>
                  {item.fechaPrevistaEnvio && (
                    <div className="order-control-table__date order-control-table__date--prevista">
                      {formatDate(item.fechaPrevistaEnvio)}
                    </div>
                  )}
                  {item.fechaEnvio && (
                    <div className="order-control-table__date order-control-table__date--envio">
                      {formatDate(item.fechaEnvio)}
                    </div>
                  )}
                  {!item.fechaPrevistaEnvio && !item.fechaEnvio && (
                    <div className="order-control-table__date order-control-table__date--secondary">
                      --
                    </div>
                  )}
                </td>
                <td>{getStatusLabel(item.estado)}</td>
                <td>{item.editorial}</td>
                <td>{item.autor}</td>
                <td>{formatCurrency(item.descuento)}</td>
                <td>{item.cantidad}</td>
              </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

OrderControlTable.propTypes = {
  filters: PropTypes.shape({
    ean: PropTypes.string,
    editorial: PropTypes.string,
    fechaPedido: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.oneOf([null])]),
    titulo: PropTypes.string,
    biblioteca: PropTypes.string,
    autor: PropTypes.string,
    estado: PropTypes.string
  }).isRequired,
  orders: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool
};

export default OrderControlTable;
